import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { selectStoreConfig } from "../redux/Slice/siteSlice";
import Icon from "../utils/icons";
import { OWNEZZY_DEMOS_URL, OWNEZZY_PLANS_URL, OWNEZZY_PRIVACY_URL, OWNEZZY_TERMS_URL } from "../utils/ownezzy";
import useDraggable from "../utils/useDraggable";
import { referralCode, trackGetFreeClick, trackTrialContinueClick } from "../utils/referral";
import { startRingSound, stopRingSound } from "../utils/ringSound";
import { trackFunnelEvent, trackActivity, flushQueue } from "../utils/activityTracker";
import SignupFlow from "../shared/signup/SignupFlow";

// "Get this website for free" on a demo store: the floating button, the offer bar and the "see other demos" prompt.
// The sign-up itself is the shared SignupFlow (shared/signup/), the same one the Ownezzy site uses.

// Ownezzy's logos: the Super Admin's (Settings -> Ownezzy logos) or the ones shipped in public/ownezzy
const ownezzyLogos = (config) => ({
    round: (config.ownezzyBranding && config.ownezzyBranding.logo) || "/ownezzy/logo-192.png",
    wide: (config.ownezzyBranding && config.ownezzyBranding.logoWide) || "/ownezzy/logo-wide.jpg",
});

// Shared controller across both the top offer bar and the floating CTA button
const ringListeners = new Set();
let isRingingGlobal = false;
let isStoppedGlobal = false;
let periodicTimer = null;
let singleRingTimer = null;
let controllerStarted = false;

function notifyRingListeners() {
  ringListeners.forEach((fn) => {
    try { fn(isRingingGlobal, isStoppedGlobal); } catch {}
  });
}

export function stopUserRing() {
  isStoppedGlobal = true;
  isRingingGlobal = false;
  if (singleRingTimer) clearTimeout(singleRingTimer);
  if (periodicTimer) clearTimeout(periodicTimer);
  singleRingTimer = null;
  periodicTimer = null;
  stopRingSound();
  try {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("oz_ring_stopped", "1");
    }
  } catch {}
  notifyRingListeners();
}

function startPeriodicRingController(ringDuration = 2000, intervalMs = 50000, initialDelay = 1500) {
  if (controllerStarted) return;
  controllerStarted = true;

  try {
    if (typeof window !== "undefined" && sessionStorage.getItem("oz_ring_stopped") === "1") {
      isStoppedGlobal = true;
      return;
    }
  } catch {}

  const triggerRing = () => {
    if (isStoppedGlobal) return;
    isRingingGlobal = true;
    notifyRingListeners();
    startRingSound(ringDuration);

    if (singleRingTimer) clearTimeout(singleRingTimer);
    singleRingTimer = setTimeout(() => {
      isRingingGlobal = false;
      stopRingSound();
      notifyRingListeners();

      // Schedule next ring after intervalMs (50s) if user has not stopped it
      if (!isStoppedGlobal) {
        periodicTimer = setTimeout(triggerRing, intervalMs);
      }
    }, ringDuration);
  };

  periodicTimer = setTimeout(triggerRing, initialDelay);
}

// Other parts of a demo store (the offer bar at the top) open the sign-up with this event
const OPEN_OFFER_EVENT = "oz:open-website-offer";
export const openWebsiteOffer = () => {
  stopUserRing();
  window.dispatchEvent(new Event(OPEN_OFFER_EVENT));
};

// CTA button periodic ring hook: rings for 2 seconds every 50 seconds (if not stopped), with an initial ring 1.5s after mount
export function usePeriodicRing(ringDuration = 2000, intervalMs = 50000, initialDelay = 1500) {
  const [state, setState] = useState({ ringing: isRingingGlobal, isStopped: isStoppedGlobal });

  useEffect(() => {
    startPeriodicRingController(ringDuration, intervalMs, initialDelay);
    const listener = (ringing, isStopped) => setState({ ringing, isStopped });
    ringListeners.add(listener);
    setState({ ringing: isRingingGlobal, isStopped: isStoppedGlobal });
    return () => {
      ringListeners.delete(listener);
    };
  }, [ringDuration, intervalMs, initialDelay]);

  return {
    ringing: state.ringing,
    isStopped: state.isStopped,
    stopUserRing,
  };
}

// The bar above a demo store's header: "Get it for free" opens the sign-up at its first step, the plans
export function OwnezzyOfferBar() {
  const { ringing, stopUserRing } = usePeriodicRing();
  return (
    <div className="ownezzy-offer-bar">
      <span>🎉 Like this store? Get one for your business.</span>
      <div className="ownezzy-offer-bar-actions">
        <a
          href={OWNEZZY_DEMOS_URL}
          target="_blank"
          rel="noopener"
          className="ownezzy-offer-bar-demos"
          onClick={() => trackActivity("click", "Top bar: Want to see other demos clicked", { url: OWNEZZY_DEMOS_URL })}
          title="Explore all 100+ demo store templates"
        >
          <span className="other-demos-pulse" />
          👀 Want to see other demos? <b>Explore 100+ stores ↗</b>
        </a>
        <button type="button" className={ringing ? "is-ringing" : ""} onClick={openWebsiteOffer}>
          Get it for free <Icon.ArrowRight />
        </button>
        {ringing && (
          <button
            type="button"
            className="ownezzy-offer-bar-stop"
            onClick={(e) => {
              e.stopPropagation();
              stopUserRing();
            }}
            title="Stop ringing"
            aria-label="Stop ringing"
          >
            <Icon.BellSlashFill /> Stop ring
          </button>
        )}
      </div>
    </div>
  );
}

export default function WebsiteOfferFlow() {
  const config = useSelector(selectStoreConfig);
  const logos = ownezzyLogos(config);
  const [open, setOpen] = useState(false);
  const ctaDrag = useDraggable("oz_offer_cta_position"); // phones: the button can be dragged out of the way
  const { ringing, stopUserRing } = usePeriodicRing();
  const [isCtaLanded, setIsCtaLanded] = useState(false);
  const [showMiddleExitModal, setShowMiddleExitModal] = useState(false); // "Not your business type?" prompt on close
  const hasOpenedRef = useRef(false);
  const ctaRef = useRef(null);

  const start = () => {
    hasOpenedRef.current = true;
    stopUserRing();
    setShowMiddleExitModal(false);
    trackGetFreeClick(config?.slug);
    setOpen(true);
  };

  // Closing before the store is made shows the "want to see other demos" prompt
  const close = ({ completed } = {}) => {
    setOpen(false);
    setIsCtaLanded(true);
    setTimeout(() => setIsCtaLanded(false), 700);
    if (!completed) {
      setShowMiddleExitModal(true);
      trackFunnelEvent("exit_intent_modal_shown", "Middle prompt shown: Want to see other demos? (modal_close)", { site: config?.slug });
      flushQueue();
    }
  };

  // The sign-up's funnel events, tagged with this demo store
  const track = (type, label, meta = {}) => {
    trackFunnelEvent(type, label, { ...meta, site: config?.slug });
    if (type === "trial_continue_click") trackTrialContinueClick(config?.slug);
    if (type !== "modal_open") flushQueue();
  };

  // On opening a demo store the sign-up opens by itself after 10 seconds, unless it was opened already
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasOpenedRef.current) start();
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  // The offer bar's "Get it for free"
  useEffect(() => {
    window.addEventListener(OPEN_OFFER_EVENT, start);
    return () => window.removeEventListener(OPEN_OFFER_EVENT, start);
  });

  return <>
    {/* Ring and shine are decoration for the button's attention animation (styles.css, "floating offer button") */}
    <button
      ref={ctaRef}
      className={`ownezzy-float-cta ${ctaDrag.isMoved ? "is-moved" : ""} ${ringing ? "is-ringing" : ""} ${isCtaLanded ? "is-landed" : ""}`}
      style={ctaDrag.style}
      {...ctaDrag.handlers}
      onClick={start}
      aria-label="Get this website for free"
    >
      <span className="ownezzy-cta-ring" aria-hidden="true" />
      <span className="ownezzy-cta-shine" aria-hidden="true" />
      <span className="ownezzy-cta-spark"><img src={logos.round} alt="" draggable={false} /></span>
      <span>Get this website<br /><b>for free</b></span>
      <Icon.ArrowUpRight size={19} className="ownezzy-cta-arrow" />
      {ringing && (
        <span
          role="button"
          tabIndex={0}
          className="ownezzy-cta-stop-ring"
          onClick={(e) => {
            e.stopPropagation();
            stopUserRing();
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.stopPropagation();
              stopUserRing();
            }
          }}
          title="Stop ringing"
          aria-label="Stop ringing"
        >
          <Icon.BellSlashFill /> Stop ring
        </span>
      )}
    </button>
    <SignupFlow
      open={open}
      onClose={close}
      template={config.template}
      layout={config.layout || ''}
      currency={config.currency || ''}
      country={config.country || ''}
      design={{ name: config.name }}
      referral={referralCode() || ""}
      logo={logos.wide}
      links={{ terms: OWNEZZY_TERMS_URL, privacy: OWNEZZY_PRIVACY_URL, plans: OWNEZZY_PLANS_URL }}
      apiBase={process.env.REACT_APP_API_URL || "/api"}
      onTrack={track}
    />
    {showMiddleExitModal && (
      <div
        className="oz-middle-exit-backdrop"
        role="presentation"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) {
            trackFunnelEvent("exit_intent_modal_dismissed", "Middle exit modal: Backdrop dismissed", { site: config?.slug });
            flushQueue();
            setShowMiddleExitModal(false);
          }
        }}
      >
        <div
          className="oz-middle-exit-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="oz-middle-exit-title"
        >
          <button
            type="button"
            className="oz-middle-exit-close"
            onClick={() => {
              trackFunnelEvent("exit_intent_modal_dismissed", "Middle exit modal: Closed with X", { site: config?.slug });
              flushQueue();
              setShowMiddleExitModal(false);
            }}
            aria-label="Close"
          >
            <Icon.X size={20} />
          </button>

          <div className="oz-middle-exit-glow" aria-hidden="true" />
          <div className="oz-middle-exit-gold-halo" aria-hidden="true" />

          <div className="oz-middle-exit-content">
            <div className="oz-middle-exit-icon-wrap">
              <span className="oz-middle-exit-icon">👀</span>
              <span className="oz-icon-sparkle">✨</span>
            </div>

            <span className="oz-middle-exit-pill oz-gold-pill-shine">
              ✨ 100+ LIVE READY-MADE STORES
            </span>
            <h3 id="oz-middle-exit-title" className="oz-middle-exit-title">
              Not your business type? Want to see other demos?
            </h3>
            <p className="oz-middle-exit-sub">
              We build high-converting live demo stores tailored for every business niche. Explore how your store can look right now:
            </p>

            <div className="oz-middle-exit-niches">
              <span className="oz-niche-pill">🧒 Kids Pattu Pavadai</span>
              <span className="oz-niche-pill">🛏️ Cotton Bedsheets</span>
              <span className="oz-niche-pill">🥻 Aari Blouses</span>
              <span className="oz-niche-pill">🪔 Brassware &amp; Urulis</span>
              <span className="oz-niche-pill">🎁 Wedding Favors</span>
              <span className="oz-niche-pill">🌸 Garland Preservation</span>
              <span className="oz-niche-pill">🧶 Crochet Flowers</span>
              <span className="oz-niche-pill">👗 Fashion &amp; Kurtis</span>
              <span className="oz-niche-pill">📱 Tech &amp; Phone Cases</span>
              <span className="oz-niche-pill">👡 Footwear &amp; Juttis</span>
              <span className="oz-niche-pill">👜 Canvas Bags &amp; Totes</span>
              <span className="oz-niche-pill">🕯️ Soy Candles &amp; Decor</span>
              <span className="oz-niche-pill">🪴 Plants &amp; Pots</span>
              <span className="oz-niche-pill">🐾 Pets &amp; Treats</span>
            </div>

            <div className="oz-middle-exit-actions">
              <a
                href={OWNEZZY_DEMOS_URL}
                target="_blank"
                rel="noopener"
                className="oz-middle-exit-btn-primary oz-btn-gold-shine"
                onClick={() => {
                  trackFunnelEvent("click", "Middle exit modal: Explore 100+ live templates clicked", { url: OWNEZZY_DEMOS_URL, site: config?.slug });
                  flushQueue();
                  setShowMiddleExitModal(false);
                }}
              >
                <span>Explore All 100+ Live Demos</span>
                <Icon.ArrowUpRight size={17} />
              </a>
              <button
                type="button"
                className="oz-middle-exit-btn-secondary"
                onClick={() => {
                  trackFunnelEvent("exit_intent_modal_dismissed", "Middle exit modal: Stayed on demo", { site: config?.slug });
                  flushQueue();
                  setShowMiddleExitModal(false);
                }}
              >
                Stay on this demo
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
  </>;
}
